// Google Maps configuration provider
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import GoogleMapsService from './GoogleMapsService';
import { GoogleMapsConfig } from './types';

interface GoogleMapsContextType {
  isLoaded: boolean;
  isLoading: boolean;
  error: string | null;
  service: typeof GoogleMapsService | null;
  loadAPI: () => Promise<void>;
}

const GoogleMapsContext = createContext<GoogleMapsContextType | undefined>(undefined);

interface GoogleMapsConfigProviderProps {
  children: ReactNode;
  config: GoogleMapsConfig;
}

export const GoogleMapsConfigProvider: React.FC<GoogleMapsConfigProviderProps> = ({
  children,
  config
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [service, setService] = useState<typeof GoogleMapsService | null>(null);

  useEffect(() => {
    setService(GoogleMapsService);
  }, []);

  const loadAPI = async () => {
    if (!service || isLoaded || isLoading) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await service.loadAPI(config.apiKey);
      setIsLoaded(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Échec du chargement de l\'API Google Maps');
      console.error('Google Maps API loading error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Auto-load API on mount if config is provided
  useEffect(() => {
    if (config.apiKey && service && !isLoaded && !isLoading) {
      loadAPI();
    }
  }, [config.apiKey, service, isLoaded, isLoading]);

  const value: GoogleMapsContextType = {
    isLoaded,
    isLoading,
    error,
    service,
    loadAPI
  };

  return (
    <GoogleMapsContext.Provider value={value}>
      {children}
    </GoogleMapsContext.Provider>
  );
};

export const useGoogleMaps = () => {
  const context = useContext(GoogleMapsContext);
  if (context === undefined) {
    throw new Error('useGoogleMaps must be used within a GoogleMapsConfigProvider');
  }
  return context;
};